import {
  Button,
  CaptionsIcon,
  CheckIcon,
  Divider,
  ForumDocIcon,
  LoadingCircleIcon,
  OpenScreenIcon,
  PollFilledIcon,
  ProfilePhoto,
  RecordIcon,
  Tooltip,
  XCloseIcon,
  palette,
} from "@minervaproject/fds";
import clsx from "clsx";
import pluralize from "pluralize";
import { useContext, useEffect } from "react";

import { AvControls } from "./av_controls";
import { CircleButton } from "./circle_button";
import { HelpMenu } from "./help_menu";
import { Menu } from "./menu";
import { MenuItem } from "./menu_item";
import { SettingsMenu } from "./settings_menu";

import { FlightErrorBoundary } from "components/error_boundary";
import {
  BreakoutTimeBar,
  BreakoutTimeRemaining,
  ClassTimeBar,
  ClassTimerTimeRemaining,
} from "components/shared/timers";
import { FORUM_URL } from "config";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useCapabilities, useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useIsMobile } from "hooks/use_is_mobile";
import { useIsWaitingForClassToStart } from "hooks/use_is_waiting_for_class_to_start";
import { useSidebarAdjustment } from "hooks/use_sidebar_adjustment";
import { useUserProfileProperties, useUserProperties } from "hooks/use_user_properties";
import { setRecordingSessionId, setRecordingState, setStagePane } from "shared/reducers/classroom";
import {
  CapabilityType,
  IconPropsType,
  PageUnloadReasonType,
  PaneContentType,
  PaneType,
  PollSessionStatusType,
  RecordingStateType,
} from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { classroomSelectors, localSelectors } from "store";
import { createMonitoringEvent } from "store/reducers/monitoring";
import { selectCurrentUserId } from "store/selectors/local";
import { getResourceIcon } from "utils/resource";
import { computeInitials, profilePhotoUrl, shortDisplayName } from "utils/user";

// TODO Resource titles are currently like "Slide for Introduction/Breakout instructions - 2108933"
// and don't neatly fit in the header. We should adjust design.
// For slides, we could maybe grab the first <h1>.
// For polls, we could still grab the title, probably
const SHAREABLE_PANE_CONTENT_TYPES = [PaneContentType.Resource, PaneContentType.PollSession];

function PaneSelect({ pane, checked }: { pane: PaneType; checked: boolean }) {
  const resource = useAppSelector(classroomSelectors.selectResourceById(pane.content_id || ""));
  const pollSession = useAppSelector(
    classroomSelectors.selectPollSessionById(pane.content_id || ""),
  );
  const poll = useAppSelector(classroomSelectors.selectPollById(pollSession?.poll_id || ""));
  let title = "";
  let IconComponent = ForumDocIcon;
  const iconProps: IconPropsType = {};
  if (pane.content_type === PaneContentType.PollSession) {
    title = poll?.title || "Poll";
    IconComponent = PollFilledIcon;
  } else if (pane.content_type === PaneContentType.Resource) {
    title = resource.title;
    IconComponent = getResourceIcon(resource);
    iconProps.color = "black";
  } else {
    return <div className="ml2 pr2">Unknown</div>;
  }
  return (
    <div className="px2 flex items-center justify-start">
      <div className={clsx("mr4", !checked && "hidden")}>
        <CheckIcon width={16} height={16} />
      </div>
      <div>
        <IconComponent width={16} height={16} {...iconProps} />
      </div>
      <div title={title} className="mb1 ml2 body-s truncate">
        {title}
      </div>
    </div>
  );
}

function Buttons() {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const isMobile = useIsMobile();
  const classId = useAppSelector(classroomSelectors.selectClassId());
  const classDashboardPath = useAppSelector(classroomSelectors.selectClassDashboardPath());
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const currentUser = useUserProfileProperties(currentUserId);
  const { is_recording_agent } = useUserProperties(currentUserId, ["is_recording_agent"]) || {};
  const { canBecomeTeacher, canControlClassroom, canStartRecording, isVisibleInClass } =
    useCapabilities();
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const hasPresentationView = useAppSelector(
    classroomSelectors.selectHasPresentationView(currentUserId),
  );
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const panes = useAppSelector(classroomSelectors.selectStage()).panes;
  const shareablePanes = panes.filter(
    (pane) => SHAREABLE_PANE_CONTENT_TYPES.includes(pane.content_type) && pane.content_id,
  );
  const presentedPanes = shareablePanes.filter((pane) => !pane.hidden_in_presentation_view);
  const isAvEnabled = useAppSelector(classroomSelectors.selectCurrentUserIsAvEnabled());
  const recordingSessionId = useAppSelector(classroomSelectors.selectRecordingSessionId());
  const recordingState = useAppSelector(classroomSelectors.selectRecordingState());
  const isRecording = recordingState === RecordingStateType.Recording;
  const preparingToRecord =
    recordingState === RecordingStateType.Preparing ||
    recordingState === RecordingStateType.Starting;
  const isNotRecording = !isRecording && !preparingToRecord;
  const recButtonTitle = isRecording
    ? "Stop Recording Class"
    : preparingToRecord
      ? "Preparing to Record..."
      : "Start Recording Class";
  const presentationButtonTooltip = hasPresentationView
    ? activeBreakoutSessionId
      ? "Presenting Breakout Group Overview"
      : `Presenting ${presentedPanes.length} ${pluralize("Pane", presentedPanes.length)}`
    : "Open Presentation View";

  const profileUrl = `${FORUM_URL}/app/admin-settings/users/${currentUserId}/edit`;
  const logoutUrl = `${FORUM_URL}/app/logout`;

  const showSettingsMenu = canControlClassroom || canBecomeTeacher || !isInPerson;

  const handleExitClass = () => {
    window.pageUnloadReason = PageUnloadReasonType.CLICKED_EXIT;
    window.location.href = `${FORUM_URL}/app/${classDashboardPath}`;
  };

  const handleStartStopRecording = async () => {
    if (isRecording && recordingSessionId) {
      dispatch(setRecordingState({ recordingState: RecordingStateType.NotRecording }));
      await forumAPI.stopRecording(recordingSessionId);
      dispatch(
        createMonitoringEvent({
          type: MonitoringEventTypeType.STOP_MANUAL_RECORDING,
          userId: currentUserId,
          contextData: { sessionId: recordingSessionId },
        }),
      );
      dispatch(setRecordingSessionId({}));
    } else if (isNotRecording) {
      dispatch(setRecordingState({ recordingState: RecordingStateType.Preparing }));
      const { id } = await forumAPI.startRecording(classId);
      dispatch(setRecordingSessionId({ id }));
      dispatch(
        createMonitoringEvent({
          type: MonitoringEventTypeType.START_MANUAL_RECORDING,
          userId: currentUserId,
          contextData: { sessionId: id },
        }),
      );
    }
  };

  if (!currentUser) {
    return null;
  }

  if (is_recording_agent) {
    return (
      <CircleButton
        tooltip="Leave Class"
        IconComponent={XCloseIcon}
        onClick={handleExitClass}
        ariaLabel="Leave Class"
      />
    );
  }

  if (isMobile) {
    return (
      <Menu
        Icon={
          <ProfilePhoto
            size="md"
            src={profilePhotoUrl(currentUser)}
            initials={computeInitials(currentUser)}
          />
        }
        ariaLabel="Leave Class"
        isMobile
      >
        <MenuItem isMobile text="Leave Class" onClick={handleExitClass} />
      </Menu>
    );
  }

  return (
    <>
      <span className="mr2">{shortDisplayName(currentUser)}</span>
      <Menu
        Icon={
          <ProfilePhoto
            size="md"
            src={profilePhotoUrl(currentUser)}
            initials={computeInitials(currentUser)}
          />
        }
        tooltip="Account"
        ariaLabel="Account Menu"
      >
        <MenuItem text="Edit Profile" href={profileUrl} target="_blank" />
        <MenuItem text="Logout" href={logoutUrl} />
      </Menu>
      {isVisibleInClass && isAvEnabled && <AvControls />}
      {showSettingsMenu && (
        <SettingsMenu
          userId={currentUserId}
          canBecomeTeacher={canBecomeTeacher}
          canControlClassroom={canControlClassroom}
          isAvEnabled={isAvEnabled}
          isVisibleInClass={isVisibleInClass}
          isInPerson={isInPerson}
        />
      )}
      <HelpMenu userId={currentUserId} />
      {canControlClassroom && isInPerson && !isPresentationView && (
        <Menu
          tooltip={presentationButtonTooltip}
          mode={hasPresentationView ? "blue" : "dark"}
          className="flex items-center justify-center"
          Icon={<OpenScreenIcon color="var(--black-tint-20)" />}
          ariaLabel={presentationButtonTooltip}
        >
          <div className="text-black body">
            {activeBreakoutSessionId ? (
              <div className="body-s px2 mb1">
                {hasPresentationView
                  ? "Presenting Breakout Group Overview"
                  : "Present Breakout Group Overview"}
              </div>
            ) : (
              <>
                <div className="body-s bold px2 mb1">
                  {shareablePanes.length > 0
                    ? "Select Panes to Present"
                    : hasPresentationView
                      ? "Presenting Stage"
                      : "Present Stage"}
                </div>
                {shareablePanes.length > 0 &&
                  shareablePanes.map((pane, index) => (
                    <div
                      key={`pane-share-${pane.content_id || index}`}
                      className={clsx(
                        "cursor-pointer py2 px0 gray-on-hover",
                        !pane.hidden_in_presentation_view && "bg-black-tint-95",
                      )}
                      onClick={() => {
                        dispatch(
                          setStagePane({
                            contentId: pane.content_id!,
                            contentType: pane.content_type,
                            index,
                            hiddenInPresentationView: !pane.hidden_in_presentation_view,
                          }),
                        );
                      }}
                    >
                      <PaneSelect checked={!pane.hidden_in_presentation_view} pane={pane} />
                    </div>
                  ))}
              </>
            )}
            {!hasPresentationView && (
              <>
                <Divider className="my1" />
                <Button
                  className="mx2 my1 nowrap"
                  onClick={() => {
                    if (!hasPresentationView) {
                      window.open("?presentation=1", "_blank");
                      dispatch(
                        createMonitoringEvent({
                          type: MonitoringEventTypeType.OPEN_PRESENTATION_VIEW,
                          userId: currentUserId,
                          contextData: { panes: shareablePanes },
                        }),
                      );
                    }
                  }}
                >
                  {activeBreakoutSessionId
                    ? "Present Overview Pane"
                    : shareablePanes.length > 0
                      ? "Present Selected Panes"
                      : "Open Presentation View"}
                </Button>
              </>
            )}
          </div>
        </Menu>
      )}
      {canStartRecording && (
        <CircleButton
          tooltip={recButtonTitle}
          IconComponent={preparingToRecord ? LoadingCircleIcon : RecordIcon}
          mode={isRecording ? "red" : "dark"}
          onClick={handleStartStopRecording}
          ariaLabel={recButtonTitle}
          disabled={preparingToRecord}
        />
      )}
      <div
        aria-hidden
        className="mx1"
        style={{ borderLeft: "1px solid var(--black-tint-20)", height: "32px" }}
      />
      <CircleButton
        tooltip="Leave Class"
        IconComponent={XCloseIcon}
        onClick={handleExitClass}
        ariaLabel="Leave Class"
      />
    </>
  );
}

function ClassroomControls() {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const { is_recording_agent } = useUserProperties(currentUserId, ["is_recording_agent"]) || {};
  const recordingSessionId = useAppSelector(classroomSelectors.selectRecordingSessionId());

  useEffect(() => {
    if (is_recording_agent) {
      window.markRecordingStarted = () => {
        dispatch(setRecordingState({ recordingState: RecordingStateType.Recording }));
        forumAPI.markRecordingAsStarted(recordingSessionId);
      };
    }
  }, [dispatch, recordingSessionId, forumAPI, is_recording_agent]);

  return (
    <div
      className="flex items-center relative"
      style={{
        transition: "right 0.1s ease-in-out",
        flex: "0 0 auto",
      }}
    >
      <Buttons />
    </div>
  );
}

function Header() {
  const title = useAppSelector(classroomSelectors.selectTitle());
  const currentUserId = useAppSelector(selectCurrentUserId());
  const branding = useAppSelector(classroomSelectors.selectBranding());
  const classDashboardPath = useAppSelector(classroomSelectors.selectClassDashboardPath());
  const { enable_live_captions } = useUserProperties(currentUserId, ["enable_live_captions"]) || {};
  const isCaptioned = useAppSelector(classroomSelectors.selectIsCaptioned());
  const captionerIsPresent = useAppSelector(classroomSelectors.selectCaptionerIsPresent());
  const widthAdjustment = useSidebarAdjustment(16);
  const captionerTooltipText = captionerIsPresent
    ? `Captioner present in class${
        enable_live_captions ? "" : "; enable captions from Settings menu"
      }`
    : "Captioner not present in class";

  const handleClickLogo = () => {
    window.pageUnloadReason = PageUnloadReasonType.CLICKED_LOGO;
    window.location.href = `${FORUM_URL}/app/${classDashboardPath}`;
  };

  return (
    <header
      className="flex items-center justify-between bg-black text-white p2"
      style={{ width: widthAdjustment }}
    >
      <div
        className="flex items-center"
        style={{ minWidth: "200px", flex: "1 1 auto", display: "block", paddingRight: "20px" }}
      >
        <h1 className="flex items-center">
          {!!branding && (
            <button
              className="button-reset cursor-pointer mr2"
              type="button"
              onClick={handleClickLogo}
            >
              <img
                alt={`${branding.page_title} Logo`}
                src={branding.logo_sm_url}
                style={{
                  height: "32px",
                }}
              />
            </button>
          )}
          <span className="body truncate ">{title}</span>
          {isCaptioned && (
            <Tooltip
              iconComponent={
                <CaptionsIcon
                  width={20}
                  height={20}
                  className="mx4"
                  color={captionerIsPresent ? palette.green : palette.blackTint40}
                  outlineColor={captionerIsPresent ? palette.green : palette.blackTint40}
                  strokeColor={palette.black}
                />
              }
              backgroundColor={palette.blackTint20}
              className="body mt2"
            >
              <div className="body-xs" style={{ maxWidth: "180px" }}>
                {captionerTooltipText}
              </div>
            </Tooltip>
          )}
        </h1>
      </div>
      <FlightErrorBoundary text="The classroom controls cannot be displayed">
        <ClassroomControls />
      </FlightErrorBoundary>
    </header>
  );
}

function MobileHeader() {
  const rawClassTitle = useAppSelector(classroomSelectors.selectTitle());
  const classTitle = rawClassTitle?.includes(" ") ? rawClassTitle.split(" ")[0] : rawClassTitle;
  const pollSessionPane = useAppSelector(classroomSelectors.selectFirstActivePollSessionPane());
  const pollSession = useAppSelector(
    classroomSelectors.selectPollSessionById(pollSessionPane?.content_id),
  );
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const pollCompleted = pollSession && pollSession.status === PollSessionStatusType.Completed;
  const classTimer = useAppSelector(classroomSelectors.selectTimer());
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const poll = useAppSelector(classroomSelectors.selectPollById(pollSession?.poll_id));
  const canSeePollResponses = canControlClassroom || (pollCompleted && poll?.student_visible);

  // The order is important, as a breakout might be ongoing while the main stage shows a poll
  const title = activeBreakoutSessionId ? "Breakout" : pollSession ? "Poll" : classTitle;

  const isWaitingToStart = useIsWaitingForClassToStart();
  // TODO Instead of having this complex logic that is duplicate of poll_session_pane/index.tsx
  // we could allow setting the subtitle via the local store. A potential issue with this is that
  // then, all pane items would need to write the store state, and timers would too
  const subtitle = isWaitingToStart ? (
    "Waiting for class to start"
  ) : activeBreakoutSessionId ? (
    <BreakoutTimeRemaining breakoutSessionId={activeBreakoutSessionId} />
  ) : pollSession && !pollCompleted && classTimer.open ? (
    <ClassTimerTimeRemaining />
  ) : pollSession && pollCompleted ? (
    pollSessionPane?.featured_poll_response ? (
      "Featured Response"
    ) : canSeePollResponses ? (
      "All Responses"
    ) : (
      "My Response"
    )
  ) : (
    ""
  );

  return (
    <header className="border-box bg-black text-white" style={{ height: "78px" }}>
      <div className="p4 flex items-center justify-between">
        <Buttons />
        <div className="flex flex-column justify-around items-center" style={{ margin: "0 auto" }}>
          <h3 className="h3 bold header-mobile-title">{title}</h3>
          <div className="body-s text-black-tint-70">{subtitle}</div>
        </div>
        {/* same width as Buttons, so that title is centered */}
        <div style={{ width: "40px" }} />
        <div />
      </div>
      {activeBreakoutSessionId ? (
        <BreakoutTimeBar breakoutSessionId={activeBreakoutSessionId} />
      ) : classTimer.open ? (
        <ClassTimeBar />
      ) : null}
    </header>
  );
}

export { Buttons, ClassroomControls, Header, MobileHeader, PaneSelect };
